/* eslint-disable react/no-unescaped-entities */
import { useCallback, useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Plus, Send, MessageSquare, Clock, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { walletApi, fmtDate } from "@/lib/wallet";
import SEO from "@/components/site/SEO";

export function CustomerSupportList() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ subject: "", body: "", priority: "normal" });
  const nav = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await walletApi.get("/customer/tickets");
      setTickets(data.items || []);
    } catch (err) {
      console.error("Could not load tickets", err);
    }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function create(e) {
    e.preventDefault();
    if (!form.subject.trim() || !form.body.trim()) return;
    setCreating(true);
    try {
      const { data } = await walletApi.post("/customer/tickets", form);
      toast.success("Ticket created");
      setForm({ subject: "", body: "", priority: "normal" });
      nav(`/app/support/${data.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create ticket");
    } finally { setCreating(false); }
  }

  return (
    <div className="min-h-screen bg-brand-bgSoft text-brand-ink" data-testid="customer-support">
      <SEO title="Support" path="/app/support" />
      <header className="bg-white border-b border-brand-line">
        <div className="container-page flex items-center justify-between h-16">
          <Link to="/app" className="inline-flex items-center gap-2 text-sm font-semibold text-brand-ink/80 hover:text-brand-ink">
            <ArrowLeft className="h-4 w-4" /> Back to dashboard
          </Link>
        </div>
      </header>
      <div className="container-page py-10 grid lg:grid-cols-3 gap-6">
        <section className="lg:col-span-2 space-y-3">
          <div className="rounded-2xl border border-brand-line bg-white p-6">
            <h1 className="font-display font-black text-2xl">Support tickets</h1>
            <p className="text-sm text-brand-muted mt-1">Our team usually responds within a few hours during UK business hours.</p>
          </div>
          {loading && <div className="text-sm text-brand-muted text-center py-12">Loading…</div>}
          {!loading && tickets.length === 0 && (
            <div className="rounded-2xl border border-dashed border-brand-line bg-white p-10 text-center" data-testid="support-empty">
              <MessageSquare className="h-8 w-8 text-brand-muted mx-auto" />
              <p className="mt-3 font-semibold">No tickets yet</p>
              <p className="text-sm text-brand-muted">Open a new one on the right to get started.</p>
            </div>
          )}
          <ul className="space-y-2">
            {tickets.map((t) => (
              <li key={t.id}>
                <Link to={`/app/support/${t.id}`} data-testid={`support-ticket-${t.id}`}
                  className="block rounded-xl border border-brand-line bg-white p-4 hover:border-brand-green">
                  <div className="flex items-center gap-3">
                    <StatusPill status={t.status} />
                    <PriorityPill p={t.priority} />
                    <span className="ml-auto text-xs text-brand-muted">{fmtDate(t.last_message_at)}</span>
                  </div>
                  <p className="mt-2 font-semibold truncate">{t.subject}</p>
                </Link>
              </li>
            ))}
          </ul>
        </section>

        <aside className="rounded-2xl border border-brand-line bg-white p-6 h-fit" data-testid="support-new-form">
          <p className="label-eyebrow">New ticket</p>
          <form onSubmit={create} className="mt-3 space-y-3">
            <input type="text" required value={form.subject} onChange={(e) => setForm({...form, subject: e.target.value})}
              placeholder="Subject" data-testid="support-subject"
              className="w-full rounded-lg border border-brand-line bg-white px-3 py-2 text-sm focus:outline-none focus:border-brand-green" />
            <textarea required value={form.body} onChange={(e) => setForm({...form, body: e.target.value})}
              placeholder="Describe your issue or question" rows={6} data-testid="support-body"
              className="w-full rounded-lg border border-brand-line bg-white px-3 py-2 text-sm focus:outline-none focus:border-brand-green" />
            <select value={form.priority} onChange={(e) => setForm({...form, priority: e.target.value})} data-testid="support-priority"
              className="w-full rounded-lg border border-brand-line bg-white px-3 py-2 text-sm focus:outline-none focus:border-brand-green">
              <option value="low">Low</option>
              <option value="normal">Normal</option>
              <option value="high">High</option>
            </select>
            <button type="submit" disabled={creating} data-testid="support-create-btn"
              className="w-full rounded-full bg-brand-green hover:bg-brand-greenHover disabled:opacity-60 text-white px-4 py-2.5 text-sm font-semibold inline-flex items-center justify-center gap-1.5">
              <Plus className="h-4 w-4" /> {creating ? "Creating…" : "Open ticket"}
            </button>
          </form>
        </aside>
      </div>
    </div>
  );
}

export function CustomerSupportThread() {
  const { ticketId } = useParams();
  const [data, setData] = useState(null);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await walletApi.get(`/customer/tickets/${ticketId}`);
      setData(data);
    } catch (err) {
      console.error("Could not load ticket", err);
    }
    finally { setLoading(false); }
  }, [ticketId]);

  useEffect(() => { load(); }, [load]);

  async function send(e) {
    e.preventDefault();
    if (!reply.trim()) return;
    setSending(true);
    try {
      await walletApi.post(`/customer/tickets/${ticketId}/reply`, { body: reply.trim() });
      setReply("");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reply failed");
    } finally { setSending(false); }
  }

  return (
    <div className="min-h-screen bg-brand-bgSoft text-brand-ink" data-testid="support-thread">
      <SEO title="Ticket" path={`/app/support/${ticketId}`} />
      <header className="bg-white border-b border-brand-line">
        <div className="container-page flex items-center justify-between h-16">
          <Link to="/app/support" className="inline-flex items-center gap-2 text-sm font-semibold text-brand-ink/80 hover:text-brand-ink">
            <ArrowLeft className="h-4 w-4" /> All tickets
          </Link>
        </div>
      </header>
      <div className="container-page py-10 max-w-3xl">
        {loading || !data ? (
          <div className="text-sm text-brand-muted">Loading…</div>
        ) : (
          <>
            <div className="rounded-2xl border border-brand-line bg-white p-6 mb-4">
              <div className="flex items-center gap-2">
                <StatusPill status={data.ticket.status} />
                <PriorityPill p={data.ticket.priority} />
              </div>
              <h1 className="font-display font-black text-2xl mt-3">{data.ticket.subject}</h1>
              <p className="text-xs text-brand-muted mt-1">Created {fmtDate(data.ticket.created_at)}</p>
            </div>

            <ul className="space-y-3" data-testid="support-messages">
              {data.messages.map((m) => (
                <li key={m.id} className={`rounded-2xl p-4 border ${m.author_role === "admin" ? "bg-brand-greenSoft/40 border-brand-greenSoft" : "bg-white border-brand-line"}`}>
                  <p className="text-xs font-semibold text-brand-muted">
                    {m.author_role === "admin" ? "OneDial Support" : "You"} · {fmtDate(m.created_at)}
                  </p>
                  <p className="mt-2 text-sm whitespace-pre-wrap">{m.body}</p>
                </li>
              ))}
            </ul>

            {data.ticket.status !== "closed" ? (
              <form onSubmit={send} className="mt-4 rounded-2xl border border-brand-line bg-white p-4">
                <textarea required value={reply} onChange={(e) => setReply(e.target.value)} rows={4}
                  placeholder="Type your reply…" data-testid="support-reply-input"
                  className="w-full rounded-lg border border-brand-line bg-white px-3 py-2 text-sm focus:outline-none focus:border-brand-green" />
                <button type="submit" disabled={sending} data-testid="support-reply-btn"
                  className="mt-3 rounded-full bg-brand-green hover:bg-brand-greenHover disabled:opacity-60 text-white px-5 py-2 text-sm font-semibold inline-flex items-center gap-1.5">
                  <Send className="h-3.5 w-3.5" /> {sending ? "Sending…" : "Send reply"}
                </button>
              </form>
            ) : (
              <p className="mt-4 text-sm text-brand-muted text-center">This ticket is closed. Open a new one from the support page if you need more help.</p>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    open: { bg: "bg-emerald-100", fg: "text-emerald-700", icon: MessageSquare },
    pending: { bg: "bg-amber-100", fg: "text-amber-700", icon: Clock },
    resolved: { bg: "bg-blue-100", fg: "text-blue-700", icon: CheckCircle2 },
    closed: { bg: "bg-slate-100", fg: "text-slate-700", icon: CheckCircle2 },
  };
  const c = map[status] || map.open;
  const Icon = c.icon;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${c.bg} ${c.fg}`}>
      <Icon className="h-3 w-3" /> {status}
    </span>
  );
}
function PriorityPill({ p }) {
  const map = {
    low: "bg-slate-100 text-slate-700",
    normal: "bg-brand-bgSoft text-brand-ink",
    high: "bg-rose-100 text-rose-700",
  };
  return <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${map[p] || map.normal}`}>{p}</span>;
}
